import { Component, ComponentType } from "@/types";
import { BaseCommandHandler, CommandResult, PropertyChange } from "../BaseCommandHandler";
import { useEditorStore } from "@/stores/editor/editor-store";

/**
 * Helper to strip # prefix from page reference
 */
function stripHashPrefix(pageName: string): string {
  return pageName.startsWith('#') ? pageName.slice(1) : pageName;
}

/**
 * IconCommandHandler - handles AI commands for Icon components
 * Supports:
 * - Navigation commands (navigate to #PageName, link to, remove navigation)
 * - Size commands (size, bigger, smaller)
 * - Color commands
 * - Rotation commands
 * - Opacity commands
 * - Stroke width commands (lucide only)
 */
export class IconCommandHandler extends BaseCommandHandler {
  constructor() {
    super();
    this.initializePatterns();
  }

  private initializePatterns(): void {
    // Navigation action patterns - support both #PageName and "PageName" syntax
    // Pattern for #PageName syntax (recommended)
    this.commandPatterns.set(
      /(?:set |make |add )?(?:icon )?navigation (?:to )?#([a-zA-Z0-9_-]+)/i,
      (match) => ({
        properties: {
          action: 'navigate',
          target: match[1] // Page name without # - will be resolved to pageId later
        }
      })
    );

    this.commandPatterns.set(
      /navigate to #([a-zA-Z0-9_-]+)/i,
      (match) => ({
        properties: {
          action: 'navigate',
          target: match[1]
        }
      })
    );

    this.commandPatterns.set(
      /(?:link|connect) to #([a-zA-Z0-9_-]+)/i,
      (match) => ({
        properties: {
          action: 'navigate',
          target: match[1]
        }
      })
    );

    this.commandPatterns.set(
      /go to #([a-zA-Z0-9_-]+)/i,
      (match) => ({
        properties: {
          action: 'navigate',
          target: match[1]
        }
      })
    );

    // Patterns for #"Page Name" or #'Page Name' syntax (quoted page names with spaces)
    this.commandPatterns.set(
      /(?:set |make |add )?(?:icon )?navigation (?:to )?#["']([^"']+)["']/i,
      (match) => ({
        properties: {
          action: 'navigate',
          target: match[1]
        }
      })
    );

    this.commandPatterns.set(
      /navigate to #["']([^"']+)["']/i,
      (match) => ({
        properties: {
          action: 'navigate',
          target: match[1]
        }
      })
    );

    this.commandPatterns.set(
      /(?:link|connect) to #["']([^"']+)["']/i,
      (match) => ({
        properties: {
          action: 'navigate',
          target: match[1]
        }
      })
    );

    this.commandPatterns.set(
      /go to #["']([^"']+)["']/i,
      (match) => ({
        properties: {
          action: 'navigate',
          target: match[1]
        }
      })
    );

    // Legacy patterns with quotes (still supported, also accept optional # prefix)
    this.commandPatterns.set(
      /(?:set |make |add )?(?:icon )?navigation (?:to )?(?:page )?#?["']([^"']+)["']/i,
      (match) => ({
        properties: {
          action: 'navigate',
          target: stripHashPrefix(match[1])
        }
      })
    );

    this.commandPatterns.set(
      /navigate to (?:page )?#?["']([^"']+)["']/i,
      (match) => ({
        properties: {
          action: 'navigate',
          target: stripHashPrefix(match[1])
        }
      })
    );

    this.commandPatterns.set(
      /(?:link|connect) to (?:page )?#?["']([^"']+)["']/i,
      (match) => ({
        properties: {
          action: 'navigate',
          target: stripHashPrefix(match[1])
        }
      })
    );

    this.commandPatterns.set(
      /go to (?:page )?#?["']([^"']+)["']/i,
      (match) => ({
        properties: {
          action: 'navigate',
          target: stripHashPrefix(match[1])
        }
      })
    );

    this.commandPatterns.set(
      /remove navigation/i,
      () => ({
        properties: {
          action: undefined,
          target: undefined,
          navigationLabel: undefined
        }
      })
    );

    // Size patterns
    this.commandPatterns.set(
      /(?:align|center)(?:\s+the)?\s+icon(?:\s+(?:in|on|within)\s+.+)?$/i,
      () => ({
        style: {
          alignSelf: 'center',
          marginLeft: 'auto',
          marginRight: 'auto',
        }
      })
    );

    this.commandPatterns.set(
      /(?:set |make )?(?:icon )?size (?:to )?(\d+)(?:px)?/i,
      (match) => {
        const size = parseInt(match[1], 10);
        // Clamp to valid range 16-128
        return { properties: { size: Math.min(Math.max(size, 16), 128) } };
      }
    );

    this.commandPatterns.set(
      /(?:make |set )?(?:icon )?(bigger|larger)/i,
      (_match, component) => {
        const currentSize = (typeof component.properties?.size === 'number')
          ? component.properties.size
          : 24;
        return { properties: { size: Math.min(currentSize + 8, 128) } };
      }
    );

    this.commandPatterns.set(
      /(?:make |set )?(?:icon )?(smaller|tinier)/i,
      (_match, component) => {
        const currentSize = (typeof component.properties?.size === 'number')
          ? component.properties.size
          : 24;
        return { properties: { size: Math.max(currentSize - 8, 16) } };
      }
    );

    // Color patterns
    this.commandPatterns.set(
      /(?:set |make |change )?(?:icon )?color (?:to )?["']?([^"']+)["']?/i,
      (match) => {
        const colorValue = this.parseColorValue(match[1]);
        return colorValue ? { properties: { color: colorValue } } : null;
      }
    );

    this.commandPatterns.set(
      /(?:make |set )?(?:icon )?(red|blue|green|yellow|orange|purple|pink|black|white|gray|grey)/i,
      (match) => ({ properties: { color: match[1].toLowerCase() } })
    );

    // Rotation patterns
    this.commandPatterns.set(
      /(?:set |make )?(?:icon )?rotation (?:to )?(\d+)(?:deg|degrees)?/i,
      (match) => {
        const rotation = parseInt(match[1], 10) % 360;
        return { properties: { rotation } };
      }
    );

    this.commandPatterns.set(
      /rotate (?:icon )?(?:by )?(\d+)(?:deg|degrees)?/i,
      (match, component) => {
        const currentRotation = (typeof component.properties?.rotation === 'number')
          ? component.properties.rotation
          : 0;
        const additionalRotation = parseInt(match[1], 10);
        return { properties: { rotation: (currentRotation + additionalRotation) % 360 } };
      }
    );

    this.commandPatterns.set(
      /remove rotation/i,
      () => ({ properties: { rotation: 0 } })
    );

    // Opacity patterns
    this.commandPatterns.set(
      /(?:set |make )?(?:icon )?opacity (?:to )?(\d+(?:\.\d+)?)/i,
      (match) => {
        const opacity = parseFloat(match[1]);
        // Normalize to 0-1 range
        const normalizedOpacity = opacity > 1 ? opacity / 100 : opacity;
        return { properties: { opacity: Math.min(Math.max(normalizedOpacity, 0), 1) } };
      }
    );

    this.commandPatterns.set(
      /(?:make |set )?(?:icon )?(transparent|translucent|opaque)/i,
      (match) => {
        const opacityMap: Record<string, number> = {
          transparent: 0,
          translucent: 0.5,
          opaque: 1
        };
        return { properties: { opacity: opacityMap[match[1].toLowerCase()] } };
      }
    );

    // Stroke width patterns (lucide only)
    this.commandPatterns.set(
      /(?:set |make )?(?:icon )?stroke(?:[-\s]?width)? (?:to )?(\d+(?:\.\d+)?)/i,
      (match) => {
        const strokeWidth = parseFloat(match[1]);
        // Clamp to valid range 0.5-4
        return { properties: { strokeWidth: Math.min(Math.max(strokeWidth, 0.5), 4) } };
      }
    );

    this.commandPatterns.set(
      /(?:make |set )?(?:icon )?(thicker|bolder)/i,
      (_match, component) => {
        const currentStroke = (typeof component.properties?.strokeWidth === 'number')
          ? component.properties.strokeWidth
          : 2;
        return { properties: { strokeWidth: Math.min(currentStroke + 0.5, 4) } };
      }
    );

    this.commandPatterns.set(
      /(?:make |set )?(?:icon )?(thinner|lighter)/i,
      (_match, component) => {
        const currentStroke = (typeof component.properties?.strokeWidth === 'number')
          ? component.properties.strokeWidth
          : 2;
        return { properties: { strokeWidth: Math.max(currentStroke - 0.5, 0.5) } };
      }
    );

    // Fill pattern (lucide only)
    this.commandPatterns.set(
      /(?:make |set )?(?:icon )?(filled|solid)/i,
      () => ({ properties: { fill: true } })
    );

    this.commandPatterns.set(
      /(?:make |set )?(?:icon )?(outlined|unfilled)/i,
      () => ({ properties: { fill: false } })
    );

    // Icon name pattern
    this.commandPatterns.set(
      /(?:set |change )?(?:icon )?(?:to |name (?:to )?)?["']([a-zA-Z0-9-_]+)["']/i,
      (match) => ({ properties: { iconName: match[1] } })
    );

    // Label pattern
    this.commandPatterns.set(
      /(?:set |add |change )?(?:icon )?label (?:to )?["']([^"']+)["']/i,
      (match) => ({ properties: { label: match[1] } })
    );
  }

  canHandle(command: string, component: Component): boolean {
    return component.type === ComponentType.ICON && this.hasIconKeywords(command);
  }

  private hasIconKeywords(command: string): boolean {
    const iconKeywords = [
      "icon", "navigate", "navigation", "link", "go to", "goto", "connect",
      "size", "color", "rotate", "rotation", "opacity",
      "center", "align",
      "bigger", "larger", "smaller", "tinier",
      "stroke", "thicker", "bolder", "thinner", "lighter",
      "filled", "solid", "outlined", "unfilled",
      "transparent", "translucent", "opaque",
      "remove", "label"
    ];
    const lowerCommand = command.toLowerCase();
    return iconKeywords.some(keyword => lowerCommand.includes(keyword));
  }

  async parseCommand(command: string, component: Component): Promise<CommandResult> {
    console.log('[IconCommandHandler] parseCommand called:', { command, componentId: component.id });

    // Split complex commands
    const commandParts = command.split(/\s+(?:and|with|also)\s+/i);
    const changes: PropertyChange = { properties: {}, style: {} };

    for (const part of commandParts) {
      // Try pattern matching
      const patternMatch = this.matchPatterns(part, component);
      if (patternMatch) {
        console.log('[IconCommandHandler] Pattern matched:', patternMatch);
        if (patternMatch.properties) {
          changes.properties = { ...changes.properties, ...patternMatch.properties };
        }
        if (patternMatch.style) {
          changes.style = { ...changes.style, ...patternMatch.style };
        }
      }

      // Parse colors directly
      const colorValue = this.parseColorValue(part);
      if (colorValue && !changes.properties?.color) {
        changes.properties = { ...changes.properties, color: colorValue };
      }
    }

    console.log('[IconCommandHandler] Collected changes:', changes);

    // Check if we found any changes
    if (Object.keys(changes.properties || {}).length > 0 ||
        Object.keys(changes.style || {}).length > 0) {

      // Resolve page name to ID for navigation actions
      if (changes.properties?.target && changes.properties?.action === 'navigate') {
        const pageTarget = changes.properties.target as string;
        console.log('[IconCommandHandler] Resolving page target:', pageTarget);
        const resolution = this.resolvePageTarget(pageTarget);
        console.log('[IconCommandHandler] Page resolution result:', resolution);

        if (resolution.error || !resolution.pageId) {
          return {
            success: false,
            error: resolution.error || `Could not resolve page "${pageTarget}"`,
            suggestions: await this.getSuggestions(component)
          };
        }

        // Update with resolved page ID and store display name
        changes.properties.target = resolution.pageId;
        changes.properties.navigationLabel = resolution.pageName;
        console.log('[IconCommandHandler] Final changes after resolution:', changes);
      }

      const validation = this.validateChanges(changes, component);
      if (!validation.valid) {
        return {
          success: false,
          error: validation.error,
          suggestions: await this.getSuggestions(component)
        };
      }

      return {
        success: true,
        command: {
          type: this.determineCommandType(changes),
          changes,
          confidence: this.calculateConfidence(command, changes),
          description: this.generateDescription(changes, component)
        },
        suggestions: await this.getSuggestions(component)
      };
    }

    return {
      success: false,
      error: "Could not understand the icon command",
      suggestions: await this.getSuggestions(component)
    };
  }

  async getExampleCommands(): Promise<string[]> {
    const pages = useEditorStore.getState().pages || [];
    const currentPageId = useEditorStore.getState().currentPageId;
    const otherPages = pages.filter(p => p.id !== currentPageId);

    // Format page name for # syntax (replace spaces with dashes for autocomplete-friendly format)
    const formatPageRef = (name: string) => name.replace(/\s+/g, '-');

    const pageExamples = otherPages.length > 0
      ? [
          `Navigate to #${formatPageRef(otherPages[0].name)}`,
          `Link to #${formatPageRef(otherPages[0].name)}`,
          'Remove navigation'
        ]
      : [];

    return [
      'Set size to 32',
      'Center icon',
      'Make icon bigger',
      'Make icon smaller',
      'Set color to blue',
      'Make icon red',
      'Set rotation to 45',
      'Rotate by 90 degrees',
      'Remove rotation',
      'Set opacity to 0.5',
      'Make icon transparent',
      'Make icon opaque',
      'Set stroke width to 3',
      'Make icon thicker',
      'Make icon thinner',
      'Make icon filled',
      'Make icon outlined',
      'Set label to "Home"',
      ...pageExamples
    ];
  }

  async getSuggestions(component: Component): Promise<string[]> {
    const suggestions: string[] = [];
    const pages = useEditorStore.getState().pages || [];
    const currentPageId = useEditorStore.getState().currentPageId;

    // Format page name for # syntax
    const formatPageRef = (name: string) => name.replace(/\s+/g, '-');

    // Navigation suggestions
    if (component.properties?.action === 'navigate') {
      suggestions.push('Remove navigation', 'Change target page');
      const targetPageId = component.properties.target as string;
      const targetPage = pages.find(p => p.id === targetPageId);
      if (!targetPage) {
        suggestions.push('Fix broken navigation');
      }
    } else {
      const otherPages = pages.filter(p => p.id !== currentPageId);
      if (otherPages.length > 0) {
        suggestions.push(
          `Navigate to #${formatPageRef(otherPages[0].name)}`,
          'Set up page navigation'
        );
      }
    }

    // Size suggestions
    suggestions.push('Center icon');

    const currentSize = (typeof component.properties?.size === 'number')
      ? component.properties.size
      : 24;
    if (currentSize < 128) {
      suggestions.push("Make icon bigger");
    }
    if (currentSize > 16) {
      suggestions.push("Make icon smaller");
    }

    // Color suggestions
    if (!component.properties?.color) {
      suggestions.push("Set color to blue");
    } else {
      suggestions.push("Change color");
    }

    // Rotation suggestions
    const currentRotation = (typeof component.properties?.rotation === 'number')
      ? component.properties.rotation
      : 0;
    if (currentRotation !== 0) {
      suggestions.push("Remove rotation");
    } else {
      suggestions.push("Rotate by 45 degrees");
    }

    // Opacity suggestions
    const currentOpacity = (typeof component.properties?.opacity === 'number')
      ? component.properties.opacity
      : 1;
    if (currentOpacity === 1) {
      suggestions.push("Make icon translucent");
    } else {
      suggestions.push("Make icon opaque");
    }

    // Stroke suggestions
    suggestions.push("Set stroke width to 3");

    // Fill suggestions
    if (component.properties?.fill) {
      suggestions.push("Make icon outlined");
    } else {
      suggestions.push("Make icon filled");
    }

    return suggestions.slice(0, 12);
  }

  protected override validateChanges(changes: PropertyChange, component: Component): { valid: boolean; error?: string } {
    // Validate size
    if (changes.properties?.size !== undefined) {
      const size = changes.properties.size;
      if (typeof size !== 'number' || size < 16 || size > 128) {
        return { valid: false, error: `Invalid size: ${size}. Must be between 16 and 128.` };
      }
    }

    // Validate opacity
    if (changes.properties?.opacity !== undefined) {
      const opacity = changes.properties.opacity;
      if (typeof opacity !== 'number' || opacity < 0 || opacity > 1) {
        return { valid: false, error: `Invalid opacity: ${opacity}. Must be between 0 and 1.` };
      }
    }

    // Validate strokeWidth
    if (changes.properties?.strokeWidth !== undefined) {
      const strokeWidth = changes.properties.strokeWidth;
      if (typeof strokeWidth !== 'number' || strokeWidth < 0.5 || strokeWidth > 4) {
        return { valid: false, error: `Invalid stroke width: ${strokeWidth}. Must be between 0.5 and 4.` };
      }
    }

    // Validate navigation action
    if (changes.properties?.action === 'navigate') {
      const targetPageId = changes.properties.target as string;
      if (!targetPageId) {
        return { valid: false, error: 'Navigation action requires a target page' };
      }

      // Check if target page exists (already validated in parseCommand, but double-check)
      const pages = useEditorStore.getState().pages || [];
      const targetPage = pages.find(p => p.id === targetPageId);

      if (!targetPage) {
        return { valid: false, error: `Target page not found: ${targetPageId}` };
      }

      // Check if navigation is allowed to target page
      if (targetPage.navigation?.allowNavigation === false) {
        return { valid: false, error: `Navigation to "${targetPage.name}" is not allowed` };
      }

      // Prevent navigation to current page
      const currentPageId = useEditorStore.getState().currentPageId;
      if (targetPageId === currentPageId) {
        return { valid: false, error: 'Cannot navigate to the current page' };
      }
    }

    return super.validateChanges(changes, component);
  }
}

export const iconCommandHandler = new IconCommandHandler();
